/**
 * A compact per-week summary of the generated plan (phase + planned hours +
 * planned hard %), persisted by the planner and read by the Training dashboard
 * so its calendar can show the same week metrics the plan view shows — the
 * dashboard itself never runs the planner. Keyed by ISO week ("YYYY-Www"), the
 * same key the planner uses for PlannedWeek.weekKey.
 */
import { PlannedWeek, PlannedSession, TrainingPhase } from "../types/training";

export const PLAN_WEEK_META_KEY = "plan-week-meta";

export interface PlanWeekMeta {
  phase: TrainingPhase;
  hours: number;    // planned target hours for the week
  hardPct: number;  // planned share of hours at Z4+ (IF ≥ 0.87)
}

export interface PlanWeeks {
  weeks: PlannedWeek[];
  sessions: PlannedSession[];
}

export function buildPlanWeekMeta(plans: PlanWeeks[]): Record<string, PlanWeekMeta> {
  const meta: Record<string, PlanWeekMeta> = {};
  for (const plan of plans) {
    const byWeek: Record<string, { hard: number; total: number }> = {};
    for (const s of plan.sessions) {
      const w = byWeek[s.weekKey] ?? (byWeek[s.weekKey] = { hard: 0, total: 0 });
      w.total += s.durationHours;
      if (s.targetIF >= 0.87) w.hard += s.durationHours;
    }
    for (const wk of plan.weeks) {
      // targetHours is set in duration mode; derive from TSS otherwise (≈60 TSS/h).
      const hours = wk.targetHours ?? Math.round((wk.targetTSS / 60) * 4) / 4;
      const w = byWeek[wk.weekKey];
      const hardPct = w && w.total > 0 ? Math.round((w.hard / w.total) * 100) : 0;
      meta[wk.weekKey] = { phase: wk.phase, hours, hardPct };
    }
  }
  return meta;
}

export function savePlanWeekMeta(plans: PlanWeeks[]): void {
  try {
    const fresh = buildPlanWeekMeta(plans);
    const existing = loadPlanWeekMeta();
    const nowKey = isoWeekKey(new Date().toISOString().slice(0, 10));
    // Preserve targets for weeks already in the PAST so a completed week keeps the
    // target it was planned with (regenerating no longer wipes history). Take the
    // fresh plan for the current + future weeks.
    const merged: Record<string, PlanWeekMeta> = {};
    for (const [k, v] of Object.entries(existing)) if (k < nowKey) merged[k] = v;
    Object.assign(merged, fresh);
    localStorage.setItem(PLAN_WEEK_META_KEY, JSON.stringify(merged));
  } catch { /* ignore */ }
}

export function loadPlanWeekMeta(): Record<string, PlanWeekMeta> {
  try { return JSON.parse(localStorage.getItem(PLAN_WEEK_META_KEY) || "{}"); } catch { return {}; }
}

// ISO week key "YYYY-Www" for a "YYYY-MM-DD" date — matches PlannedWeek.weekKey.
export function isoWeekKey(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}
